/**
 * Where a dragged trim handle is allowed to land.
 *
 * Three constraints pull on the handle and they can disagree, so what matters is which one wins.
 * They used to be applied in the order they were written, which put the weakest last: a handle
 * dragged while the strip's measurements were momentarily stale could be pulled clean through
 * the opposite handle, leaving a moment that ended before it began — `44:21 – 0:26` from a
 * two-second moment at 44:21.
 *
 * In strict order, weakest first:
 *
 * 1. **Stay on screen.** Advisory. It is derived from `scrollLeft` (pixels) against
 *    `timelineWidth` (pixels), and those two are not always from the same moment: zooming
 *    changes the width during render while the scroll position is React state that catches up a
 *    frame later, and the strip auto-zooms hardest on exactly the short moments this matters
 *    for. A window computed from a mismatched pair is nonsense, so it is never allowed to
 *    overrule anything below it.
 * 2. **Do not cross the other handle.** The moment keeps a minimum length — see
 *    `calculateMinimumDuration`, which sizes it so the head stays grabbable at the current zoom.
 * 3. **Stay inside the media.** Physical, and therefore last: a moment cannot run past the end
 *    of the file, even if honouring the minimum length would need it to.
 *
 * Pure and DOM-free so the orderings can be tested directly — see `__tests__`.
 */

import { timeAtPixel } from '../../timelineGeometry';

const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value));

/** The two ends of a moment, in absolute media seconds. */
export interface MomentBounds {
  start: number;
  end: number;
}

export interface TrimGeometry {
  /** Full scrollable width of the strip in pixels, at the current zoom. */
  timelineWidth: number;
  /** Width of the strip's visible viewport in pixels. */
  visibleWidth: number;
  /** Media duration in seconds. */
  duration: number;
}

/**
 * The span of the media on screen right now, in seconds.
 *
 * `null` where there is no window to speak of rather than a degenerate one: before the strip has
 * been measured, `visibleWidth` is zero, and `[scrollLeft, scrollLeft + 0]` read as a window
 * would pin every drag to a single instant near the top of the media. That is the shape the
 * inverted-moment bug took.
 */
export const visibleTimeSpan = (
  scrollLeft: number,
  { timelineWidth, visibleWidth, duration }: TrimGeometry,
): MomentBounds | null => {
  if (timelineWidth <= 0 || visibleWidth <= 0 || duration <= 0) return null;
  const geometry = { timelineWidth, duration };
  return {
    start: clamp(timeAtPixel(scrollLeft, geometry), 0, duration),
    end: clamp(timeAtPixel(scrollLeft + visibleWidth, geometry), 0, duration),
  };
};

/**
 * Where the named handle lands when the pointer asks for `requested` seconds.
 *
 * Returns the one bound that moved; the caller keeps the other. The result is always inside the
 * media and always on the correct side of the opposite handle, whatever the window says — those
 * are the two properties the tests pin.
 */
export const trimHandleTime = (
  handle: 'start' | 'end',
  requested: number,
  moment: MomentBounds,
  {
    minDuration,
    mediaDuration,
    visible,
  }: { minDuration: number; mediaDuration: number; visible: MomentBounds | null },
): number => {
  const onScreen = visible ? clamp(requested, visible.start, visible.end) : requested;

  if (handle === 'start') {
    // `Math.max(…, 0)` on the upper bound, not a separate clamp: on a media shorter than the
    // minimum length the two constraints cannot both hold, and an upper bound below the lower
    // one would make `clamp` return whichever Math call came last.
    return clamp(onScreen, 0, Math.max(moment.end - minDuration, 0));
  }
  return clamp(onScreen, Math.min(moment.start + minDuration, mediaDuration), mediaDuration);
};
